import { getRegionConfig } from 'config';
import { contentfulClient } from 'connectors/contentful';
import { generateLink } from 'connectors/utils/generate-link';
import { getEntryId, isEntryHyperlink } from 'connectors/utils/resolve-rich-text-node';
import { Region } from 'constants/shop';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import { getUrlLocalePrefix } from 'utils/i18n/locale-path-prefix';
import { withLocalePathPrefix } from 'utils/i18n/with-locale-path-prefix';

const getString = (value: unknown, path: string): string | undefined => {
  const result: unknown = get(value, path.split('.'));
  return typeof result === 'string' ? result : undefined;
};

const getCategory = (handle: string | undefined): { fields: { handle: string } } | undefined =>
  handle ? { fields: { handle } } : undefined;

/** Read only the routing fields; a target can also be an unresolved SDK Link. */
const getPageLink = (target: unknown): string => {
  const handle = getString(target, 'fields.handle');
  const contentType = getString(target, 'sys.contentType.sys.id');
  if (!handle || !contentType) {
    return '';
  }

  return generateLink({
    sys: { contentType: { sys: { id: contentType } } },
    fields: {
      handle,
      mainCategory: getCategory(getString(target, 'fields.mainCategory.fields.handle')),
      subCategory: getCategory(getString(target, 'fields.subCategory.fields.handle')),
    },
  });
};

const getTargetLink = (target: unknown): string => {
  if (getString(target, 'sys.contentType.sys.id') !== 'internalLink') {
    return getPageLink(target);
  }

  const reference = get(target, 'fields.reference');
  const pathSuffix = getString(target, 'fields.pathSuffix');
  const base = getPageLink(reference);

  // Match the CMS path field: collection filters, optionally joined with +.
  if (!base || !pathSuffix || !/^\/[a-z0-9]+(?:[-+/][a-z0-9]+)*$/.test(pathSuffix)) {
    return '';
  }

  return `${base.replace(/\/$/, '')}${pathSuffix}`;
};

/** Collect once, then fetch missing regional targets in batches shared by text and buttons. */
export const loadRichTextLinks = async ({
  node,
  region,
  locale,
}: {
  node: unknown;
  region: Region;
  locale?: string;
}): Promise<Partial<Record<string, string>>> => {
  const defaultLocale = getRegionConfig(region).locale;
  const regionLocale = getUrlLocalePrefix(defaultLocale);
  const links: Partial<Record<string, string>> = {};
  const contentTypes: Partial<Record<string, string>> = {};
  const ids: string[] = [];
  // SDK includes can contain shared objects and cycles.
  const seen = new WeakSet<object>();
  const collect = (value: unknown): void => {
    if (!isObject(value) || seen.has(value)) {
      return;
    }
    seen.add(value);

    if (isEntryHyperlink(value)) {
      const id = getEntryId(value.data.target);
      if (!id) {
        return;
      }
      const contentType = getString(value.data.target, 'sys.contentType.sys.id');
      if (contentType) {
        contentTypes[id] = contentType;
      }
      const link = locale === regionLocale ? getTargetLink(value.data.target) : '';
      if (link) {
        links[id] = withLocalePathPrefix(link, locale, defaultLocale);
      }
      if (!ids.includes(id)) {
        ids.push(id);
      }
      // A hyperlink points to another page, not content to render on this page.
      return;
    }

    for (const [key, child] of Object.entries(value)) {
      if (key !== 'sys' && key !== 'reference') {
        collect(child);
      }
    }
  };
  collect(node);

  const missingIds = ids.filter((id) => !links[id]);
  // Bound both the query length and the result count for pages with many links.
  const batchSize = 100;
  for (let offset = 0; offset < missingIds.length; offset += batchSize) {
    const batch = missingIds.slice(offset, offset + batchSize);
    const contentType = contentTypes[batch[0]];
    const sameContentType = contentType && batch.every((id) => contentTypes[id] === contentType);
    // Articles need categories; internal links need their referenced page.
    const needsReferences = batch.some(
      (id) =>
        !contentTypes[id] || contentTypes[id] === 'blogPost' || contentTypes[id] === 'internalLink'
    );
    try {
      const { items } = await contentfulClient.getEntries<unknown>({
        'sys.id[in]': batch.join(','),
        locale: regionLocale,
        include: needsReferences ? 1 : 0,
        // Contentful requires a single content_type for field-level selection.
        ...(sameContentType
          ? {
              content_type: contentType,
              select:
                contentType === 'blogPost'
                  ? 'sys,fields.handle,fields.mainCategory,fields.subCategory'
                  : contentType === 'internalLink'
                    ? 'sys,fields.reference,fields.pathSuffix'
                    : 'sys,fields.handle',
            }
          : {}),
        limit: batch.length,
      });
      for (const entry of items) {
        const link = getTargetLink(entry);
        if (link) {
          links[entry.sys.id] = withLocalePathPrefix(link, locale, defaultLocale);
        }
      }
    } catch (error) {
      console.warn('[contentful] Failed to resolve rich-text links', batch, error);
    }
  }

  return links;
};
